import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";
import { Box, Link, ListItemIcon, ListItemText, MenuItem } from "@mui/material";

import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

export const purchaseSeatLanguages = {
  progressBarSuccessText:
    "You're good to go! Add more events or select Continue to view seating options.",
};

export const NewYorkYankeesLink = () => (
  <Link
    target="_blank"
    href="https://am.ticketmaster.com/nyyreg/"
    sx={{
      fontWeight: 500,
      color: "primary.main",
      textDecoration: "none",
    }}
  >
    Account Manager
  </Link>
);

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description: (
      <>
        Click Continue and connect your New York Yankees <NewYorkYankeesLink />{" "}
        using our secure Ticketmaster login.
      </>
    ),
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your <NewYorkYankeesLink /> within 24 hours
        of redemption.
      </>
    ),
  },
];

export const faqs = [
  {
    title: `How does the New York Yankees Flex Plan work?`,
    description:
      "The New York Yankees Flex Plan allows you to select 5+ games from various tiers. You may select a minimum of 4 games from the Silver tier and 1 game from the Gold tier. Choose the Yankees home games that best fit your schedule and select any available seats for each game—giving you flexibility and control over your season!",
  },
  {
    title: "Do Yankees Flex Plans come with any benefits?",
    description: (
      <>
        Yes! Yankees Flex Plan benefits include:
        <ul>
          <li>Savings off the advance ticket price</li>
          <li>Access to the best locations and the biggest rivals</li>
          <li>Flexibility to pick games that fit your schedule</li>
          <li>Postseason presale opportunity</li>
          <li>
            Access to{" "}
            <Link
              target="_blank"
              href="https://www.mlb.com/yankees/tickets/season-tickets/holders/legacy-club-rewards"
              sx={{
                fontWeight: 500,
                color: "primary.main",
                textDecoration: "none",
              }}
            >
              Yankees Legacy Club Rewards
            </Link>
          </li>
          <li>Dedicated Yankees representative</li>
          <li>
            Online account management via{" "}
            <Link
              target="_blank"
              href="https://www.mlb.com/yankees/tickets/season-tickets/holders/account-manager"
              sx={{
                fontWeight: 500,
                color: "primary.main",
                textDecoration: "none",
              }}
            >
              yankees.com/myyankees
            </Link>
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description: (
      <>
        Event tickets are delivered directly within your New York Yankees{" "}
        <NewYorkYankeesLink />.
      </>
    ),
  },
  {
    title:
      "Am I guaranteed a promotional item if I purchase tickets to that game?",
    description: (
      <>
        Purchasing a ticket for any promotional date does not guarantee that a
        Guest will receive the designated giveaway item. Distribution of
        promotional items is for Guests in attendance only, while supplies last.
        All promotional dates, items and distribution are subject to change
        and/or cancellation. For complete details on giveaway items and event
        dates, please review the{" "}
        <Link
          href="https://www.mlb.com/yankees/tickets/promotions/schedule"
          target="_blank"
        >
          full Yankees Promotional Schedule
        </Link>{" "}
        and{" "}
        <Link href="https://www.mlb.com/yankees/tickets/promotions/schedule#guidelines">
          Guidelines for Yankee Stadium Promotional Item Distribution
        </Link>
        .
      </>
    ),
  },
];

export const NewYorkYankeesTCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/yankees/tickets/terms-and-conditions",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>New York Yankees</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export function AnalyticsHeader() {
  return (
    <>
      <MLBAdobeScriptHeader />
    </>
  );
}

export function NewYorkYankeesRegistrationTerms() {
  return (
    <>
      In connection with your registration, you will receive ticket-related
      commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. Further, due to the COVID-19 pandemic, scheduling may be
      impacted; games may be canceled, rescheduled, or postponed or games may be
      held with limited capacity. Upon completing your purchase by selecting
      Purchase Tickets you acknowledge and agree that your credit card will be
      charged. By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Yankees account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only.{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}

export function NewYorkYankeesCheckoutTerms() {
  return (
    <>
      In connection with your purchase, you will receive ticket-related
      commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>
      . You may unsubscribe at any time. By checking this box you agree to
      receive additional commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>
      . The terms and conditions of your ticket and policies applicable to its
      use are updated to address circumstances related to COVID-19 and health
      and safety. Further, due to the COVID-19 pandemic, scheduling may be
      impacted; games may be canceled, rescheduled, or postponed or games may be
      held with limited capacity. Upon completing your purchase by selecting
      Purchase Tickets you acknowledge and agree that your credit card will be
      charged. By clicking “Purchase Tickets”, you agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>
      ,{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>{" "}
      and to receive your ticket(s) via an SMS text message to the mobile #
      associated with your Yankees account. Message and data rates may apply
      Tickets will be made available in the MLB Ballpark mobile application
      only.{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/"
      >
        MLB.com
      </Link>{" "}
      and supported mobile device required.
    </>
  );
}
